import {
  Component,
  type ReactNode,
  Suspense,
  createContext,
  use,
  useContext,
  useEffect,
} from "react";

/**
 * DeferredHydration — takes its subtree out of the initial hydration task.
 *
 * @deprecated Quarantined, no call sites (COM-186). Wrapping a Builder
 * `<Content>` subtree in this gate blanks it for ~550 ms and costs CLS 0.303,
 * and no TBT benefit was ever demonstrated. An eslint `no-restricted-imports`
 * rule blocks the import. Exit condition is NOT the SDK upgrade — see the
 * COM-186 note below. Kept, rather than deleted, because the mechanism
 * write-up below is the artifact worth preserving. See
 * docs/working-conventions.md.
 *
 * Problem: React can only split hydration work at Suspense boundaries. A
 * full-page Builder `<Content>` tree (every registered custom component)
 * with no boundary hydrates as ONE uninterruptible main-thread task — the
 * page paints fast but nothing is clickable until the whole task finishes
 * (menu dead, minicart dead, clicks queued).
 *
 * Mechanism: a Suspense boundary whose content suspends on a module-level
 * gate promise during client hydration. While suspended, React skips
 * hydrating the boundary and keeps the server-rendered DOM visible
 * (dehydrated-boundary semantics). A sibling releaser hydrates with the
 * early pass and resolves the gate on the first idle period — the subtree
 * then hydrates on React's retry lane: interruptible, time-sliced, yielding
 * to input between slices.
 *
 * `fallback={children}`: the intent was that if anything ever forces the
 * boundary to client-render before release (e.g. a sync context update
 * descending into it), the fallback IS the real content — worst case a
 * duplicate render, never a blank region. **Both halves of that are wrong
 * when the subtree contains its own suspending blocks** (a Builder footer
 * does — see the CTA `<Await>` note below):
 *   - SSR is NOT inert. React emits the fallback in the slot AND a pending
 *     boundary marker (`<!--$?--><template id="B:…">`), then streams a second
 *     copy of the subtree in the tail. Measured on a homepage: two 137 KB
 *     copies of the footer tree.
 *   - A forced client-render is NOT a duplicate render. React discards the
 *     server DOM and re-renders from scratch, and while that is in flight the
 *     region is BLANK — ~550 ms of it (COM-186).
 * See the COM-186 note above `DEFER_HYDRATION` and docs/working-conventions.md.
 *
 * Load-bearing invariant for consumers: post-mount setStates in context
 * providers ABOVE this boundary must be wrapped in `startTransition`. A
 * sync update descending into a dehydrated boundary makes React abandon
 * selective hydration and process the whole subtree at urgent priority —
 * re-fusing exactly the long task this component exists to split. See the
 * fetcher→state sync in `app/routes/resources/favorites.tsx`.
 *
 * Costs: React event handlers inside the subtree are inert until release
 * (~first idle, capped at RELEASE_TIMEOUT_MS). Native anchors work
 * throughout; `onClick`s are queued via React event replay. Fine for
 * link-dominated pages (homepage, Builder landing pages); think twice
 * before gating interaction-dense routes (PDP buy box).
 */

const RELEASE_TIMEOUT_MS = 1500;

// Module-level singleton gate: one release covers every boundary on the
// page, and subsequent soft navigations render clean (released === true →
// no suspension, zero cost).
let released = false;
let releaseFn: (() => void) | undefined;
const gate = new Promise<void>(resolve => {
  releaseFn = resolve;
});

function release() {
  if (released) return;
  released = true;
  releaseFn?.();
}

function HydrationGate() {
  // Server: inert (boundary never suspends during SSR, content is emitted).
  // Client before release: suspend, so React skips hydrating the enclosing
  // boundary and keeps the server DOM visible.
  if (typeof window !== "undefined" && !released) use(gate);
  return null;
}

function ReleaseHydrationGate() {
  // Sibling of the boundary — never a descendant (its effect would deadlock
  // waiting on its own gate). Hydrates with the early pass; releases on the
  // first idle so the gated subtree hydrates at retry-lane priority.
  useEffect(() => {
    if (released) return;
    if (typeof requestIdleCallback === "function") {
      requestIdleCallback(release, { timeout: RELEASE_TIMEOUT_MS });
    } else {
      // Safari has no requestIdleCallback — yield a beat past the chrome
      // hydration commit instead.
      setTimeout(release, 250);
    }
  }, []);
  return null;
}

// SFCC-11887 (why the gate is OFF by default): the Suspense + `use(gate)`
// deferral throws React #482 ("async Client Component") DURING hydration of
// the Builder `<Content>` subtree on real Chrome under CPU load — a race
// between the retry-lane hydration of the dehydrated boundary and the Builder
// SDK `<Content>`'s internal render-phase thenable (`@builder.io/sdk-react`
// 4.x). Because it is a hydration-phase error it escalates PAST nested error
// boundaries to `RootErrorBoundary` → the whole-site "services offline" screen
// (verified on the PR #676 deploy-preview: the wrapping
// `BuilderContentBoundary`'s componentDidCatch NEVER fired — see commits
// d9b04c57 / cf01fd20). The throw is always at `use(gate)` and nowhere else,
// so the deferral mechanism itself is what produces it.
//
// The gate is therefore env-gated FAIL-CLOSED via
// `WORKERS_DEFER_HYDRATION_MODE` (unset / anything but "on" ⇒ off — see
// `app/hec/config/integration-flags.ts` and
// `docs/integration-kill-switches.md`). PREREQUISITE before flipping "on"
// anywhere above a canary tier: eliminate the racing thenable — upgrade
// `@builder.io/sdk-react` 4.x → 5.x (the real React-19 fix) or ship an
// alternative crash-safe split — and re-verify with the documented repro
// (Playwright channel:chrome + 6x CPU throttle, homepage, ~1-in-10 loads).
// `DeferredHydrationBoundary` below contains ORDINARY render errors inside
// the gated subtree during a canary, but it can NOT contain the
// hydration-phase #482 (that was exactly PR #676's disproven approach).
//
// Plumbing invariant: the enabling value comes from React context, seeded by
// `DeferredHydrationProvider` from SERIALIZED LOADER DATA (routes/layout.tsx).
// The gate changes SSR HTML structure (Suspense markers + `fallback={children}`
// duplicating the subtree markup in the shell), so client hydration must use
// exactly the value the server rendered with. Never read the flag from a
// client-side env/global — a server/client disagreement is itself a hydration
// mismatch. Context default is `false`, so any usage outside the provider is
// an inert pass-through (fail-closed all the way down).
//
// COM-186 — the reason this component now has NO call sites. This is the same
// dehydrated-boundary abandonment SFCC-12061 documented, at a much larger
// scale: there it was a `fallback={null}` Await boundary blanking the footer,
// here it is the gate blanking its whole subtree.
//
// Chain, as measured on the batch-2 integration preview with the gate on:
//   1. The gate leaves its subtree DEHYDRATED on the client (`use(gate)`
//      suspends until first idle), so the server DOM is what's on screen.
//   2. A provider-level state update lands inside that dehydrated window.
//      React abandons the boundary, DISCARDS the server DOM, and client-
//      renders the subtree from scratch — ~550 ms of blank for a 137 KB
//      Builder tree. This is the "load-bearing invariant" at the top of this
//      file, and the failure mode use-failsoft-resource.ts already describes.
//   3. While the body is blank the footer is pulled into the viewport and back
//      out — CLS 0.303, ~15 perf points on home.
// Timing is why it hid for so long: with a ~250 ms (edge-cached) document the
// provider mount effects fall INSIDE the dehydrated window; with a ~1.8 s
// document they land after `release()` and nothing is abandoned.
//
// Two things NOT to conclude from this:
//   - It is not the Builder SDK. `@builder.io/sdk-react` 4.2.4 contains zero
//     `Suspense` and `ContentComponent` is a plain sync component. The SSR
//     thenables in a footer are OURS — `<Suspense><Await resolve={pageRef}>`
//     in blocks/components/cta/link.tsx + linkWrapper.tsx, fed by a
//     `Map<string, Promise<…>>`. The 4.x -> 5.x upgrade above is the exit
//     condition for the #482 race, NOT for this. Do not spend a sprint on it
//     expecting the blank to go away.
//   - It is not out-of-order streaming per se. develop already streams ~162
//     pending boundaries per homepage, 33 of them inside the footer, and
//     `$RC` swaps them without blanking anything. The gate adds ONE more —
//     the largest, enclosing the whole subtree — and the damage is done on
//     the client, not by the swap.
//
// SCOPE OF THE BAN — narrow, and easy to over-read. What is unsafe is a
// HYDRATION-DEFERRAL boundary: one that suspends on the client via the
// module-level `use(gate)` thenable below. An ordinary SSR-streaming
// `<Suspense>` around a Builder `<Content>` is a supported, deliberate
// containment pattern and is used on purpose — see
// routes/product-list/shop.$categoryId.tsx, routes/product-details/
// product-details.tsx, and hec/builder/blocks/components/
// BuilderReferenceContent.tsx. Do not remove those.
//
// Enabling the flag is therefore no longer sufficient to restore the gate —
// there are no call sites left for it to act on. Restoring needs new call
// sites AND either the subtree's own SSR-suspending blocks made synchronous
// (inline every CTA reference's `value.data` before render) or an accepted
// duplicate-markup + client-re-render cost. An eslint `no-restricted-imports`
// rule blocks the `DeferredHydration` import meanwhile. Full evidence:
// docs/working-conventions.md, "Never put a hydration-deferral boundary
// around a Builder <Content> tree".
const DeferredHydrationEnabledContext = createContext(false);

/**
 * Seeds the gate's enablement for an entire subtree. Rendered once per layout
 * from `routes/layout.tsx` with the loader-resolved
 * `WORKERS_DEFER_HYDRATION_MODE` value (identical on server and client because
 * it travels through loader data).
 */
export function DeferredHydrationProvider({
  enabled,
  children,
}: {
  enabled: boolean;
  children: ReactNode;
}) {
  return (
    <DeferredHydrationEnabledContext.Provider value={enabled}>
      {children}
    </DeferredHydrationEnabledContext.Provider>
  );
}

/**
 * Single-shot containment for the GATED branch. On any render error inside
 * the deferred subtree it degrades to the plain, ungated children (a normal
 * client render — same markup, no Suspense gate) instead of letting the error
 * reach `RootErrorBoundary`.
 *
 * Honest scope (SFCC-11887): this contains post-hydration render errors and
 * client-render errors during a canary. It does NOT contain the
 * hydration-phase #482 race itself — React surfaces that class of error past
 * nested boundaries (empirically verified, PR #676). It is defense-in-depth
 * for the canary, not the fix; the fix is the SDK upgrade (see above).
 *
 * Exported for unit tests only.
 */
export class DeferredHydrationBoundary extends Component<
  { children: ReactNode; fallback: ReactNode },
  { crashed: boolean }
> {
  state = { crashed: false };

  static getDerivedStateFromError() {
    return { crashed: true };
  }

  componentDidCatch(error: unknown) {
    // Visibility only — never rethrow, never block the fallback render.
    console.error("[deferred-hydration] gated subtree crashed", error);
  }

  render(): ReactNode {
    return this.state.crashed ? this.props.fallback : this.props.children;
  }
}

export function DeferredHydration({ children }: { children: ReactNode }) {
  const enabled = useContext(DeferredHydrationEnabledContext);
  if (!enabled) {
    return <>{children}</>;
  }
  return (
    <DeferredHydrationBoundary fallback={children}>
      <ReleaseHydrationGate />
      <Suspense fallback={children}>
        <HydrationGate />
        {children}
      </Suspense>
    </DeferredHydrationBoundary>
  );
}
